"use client";

import { DocumentData, QueryDocumentSnapshot } from "firebase/firestore";
import Link from "next/link";
import { useEffect, useState } from "react";
import QuestionSVG from "~/public/images/question.svg";
import ErrorUI from "~/src/components/common/ErrorUI";
import LoadingUI from "~/src/components/common/LoadingUI";
import ReadMore from "~/src/components/common/ReadMore";
import IMobileResponsiveBigBannerAd from "~/src/components/feature/ad/IMobileResponsiveBigBannerAd";
import AnalysisHeading from "~/src/components/feature/analysis/info/AnalysisHeading";
import NoneAnalysis from "~/src/components/feature/mypage/analysis/NoneAnalysis";
import ImageHeader from "~/src/components/feature/top/ImageHeader";
import {
  AD_ELEMENT_ID_BIG_BANNER_1,
  AD_ELEMENT_ID_SP_BIG_BANNER_1,
  ASID_BIG_BANNER_1,
  ASID_SP_BIG_BANNER_1,
} from "~/src/const/ad/imobile";
import { readLatestAnalysisList, ReadPartQueryResult } from "~/src/lib/firestore/analysis";
import { useAuthContext } from "~/src/stores/context/auth/AuthContext";
import { HeadingAnalysisArticle } from "~/src/type/ui/article/article";

//------------
// 最新分析記事読み込み数上限
//------------
const RESENT_INIT_READ_SIZE = 16;
const RESENT_MORE_READ_SIZE = 16;
const MAX_READ_SIZE = 100;

/*
 * トップページ
 *   最新記事の一覧表示の流れ
 *     ・firestoreから最新記事の一覧を取得
 *     ・最新記事の取得後、ユーザーのいいね情報を取得。最新記事リストにいいね状態を反映。
 *     ※ログイン状態が変化する毎に、いいね状態を更新する
 */
export default function TopPage() {
  // const router = useRouter();
  const { info } = useAuthContext();
  const [analysisList, setAnalysisList] = useState<HeadingAnalysisArticle[] | null | undefined>(
    undefined,
  ); // 最新の分析記事リスト
  const [moreUi, setMoreUi] = useState(true); // 追加表示UIの表示制御
  const [isMoreLoading, setIsLoading] = useState(false); // 追加表示UI向けのローディング制御
  const [justBeforeArticle, setJustBeforeArticle] = useState<
    QueryDocumentSnapshot<DocumentData, DocumentData> | undefined
  >(undefined); // 取得した最後の分析記事ドキュメント

  /*
   * 最新記事の一覧を取得
   */
  useEffect(() => {
    //------------
    // 最新記事の取得
    //------------
    // if (analysisList.length == 0) {
    if (!analysisList) {
      fetchTopArticle();
    }
  }, []); // eslint-disable-line

  /*
   * 最新記事取得
   */
  const fetchTopArticle = async () => {
    try {
      //------------
      // 取得
      //------------
      // firestoreから取得
      const readResult = await readLatestAnalysisList(RESENT_INIT_READ_SIZE);

      //------------
      // フックに設定
      //------------
      // 取得した最後の分析記事ドキュメント
      setJustBeforeArticle(readResult.readLast);
      // 表示する最新記事リスト
      setAnalysisList(readResult.list);

      //------------
      // 追加読み込みUI制御
      //------------
      // 初回読み込み最大数未満の場合、非表示
      if (readResult.list.length < RESENT_INIT_READ_SIZE) {
        setMoreUi(false);
      }
      // debugger;
    } catch (error) {
      console.log("最新記事一覧取得エラー error", error);

      //------------
      // 取得エラー
      //------------
      setAnalysisList(null);
    }
  };

  /*
   * 最新記事取得（続きから）
   */
  const fetchMoreArticle = async () => {
    try {
      //------------
      // 読み込み
      //------------
      // 続きのドキュメントを読み取り
      const readResult: ReadPartQueryResult = await readLatestAnalysisList(
        RESENT_MORE_READ_SIZE,
        justBeforeArticle!,
      );
      setJustBeforeArticle(readResult.readLast);

      // 追加読み込みUIのローディングUIを解除
      setIsLoading(false);

      //------------
      // 読み込みなし
      //------------
      if (!readResult.readLast || !analysisList) {
        // 読み込みUIを非表示にする
        setMoreUi(false);
        return;
      }

      //------------
      // 読み込みあり
      //------------
      // リスト表示上限まで取得した場合、「読み込みUI」を非表示にする
      const currentListNum = analysisList.length + readResult.list.length;
      if (currentListNum >= MAX_READ_SIZE) {
        setMoreUi(false);
      }

      // 正常に取得した場合、表示リストに追加
      setAnalysisList((pre) => [...pre!, ...readResult.list]);
    } catch (error) {
      console.log("最新記事一覧取得エラー error", error);

      //------------
      // 取得エラー
      //------------
      setAnalysisList(null);
    }
  };

  // /*
  //  * 分析記事カードクリック処理
  //  */
  // const onClickArticle = (article: HeadingAnalysisArticle) => {
  //   //------------
  //   // 画面遷移
  //   //------------
  //   const url = getDetailAnalysisUrl(article.id);
  //   router.push(url);
  // };

  /*
   * 分析記事の追加読み込み
   */
  const handleLoadMore = () => {
    //------------
    // 最新記事の取得
    //------------
    if (justBeforeArticle) {
      // 追加読み込みUIのローディングUIを表示
      setIsLoading(true);
      // 記事追加読み込み
      fetchMoreArticle();
    }
  };

  return (
    <>
      {/* トップページ上部イメージ */}
      <ImageHeader />

      {/* メインコンテンツ */}
      <div className="pt-12 md:pt-20">
        {/* 最新の分析記事一覧（幅制限あり） */}
        <div className="mx-auto flex w-full flex-col px-7 md:max-w-screen-lg lg:max-w-screen-xl">
          <div>
            <p className="text-xs tracking-widest text-GRAY_2">最新の分析記事</p>
            <h2 className="mt-2 text-2xl font-bold">New Analysis</h2>
            <div className="mt-7">
              {/* エラー */}
              {analysisList === null && <ErrorUI parentClassName="pb-40 pt-16" />}

              {/* ロード中 */}
              {analysisList === undefined && <LoadingUI />}

              {/* 分析記事なし */}
              {analysisList?.length === 0 && (
                <NoneAnalysis
                  image={QuestionSVG}
                  message={"現在、投稿されている記事はありません。"}
                />
              )}

              {/* 分析記事一覧 */}
              {analysisList && (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {analysisList!.map((article) => {
                    const liked = info?.favoritList.includes(article.id) ?? false;
                    return (
                      <AnalysisHeading
                        key={article.id}
                        liked={liked}
                        article={article}
                      />
                    );
                  })}
                  {moreUi && (
                    <div className="md:col-span-2">
                      <ReadMore
                        onClick={handleLoadMore}
                        isLoading={isMoreLoading}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Creator Influence Map / Creator's Insight（フルワイド横並び） */}
        {/* <div className="mt-16 border-t border-GRAY_3 bg-gradient-to-br from-black/70 from-5% via-[#A594F9]/80 to-white"> */}
        {/* <div className="mt-16 border-t border-GRAY_3 bg-[#E5E1D6]/40"> */}
        {/* Influence Map バナーカード */}
        {/* <div className="px-7 py-12 md:px-12 lg:px-20">
          <Link
            href="/influence"
            className="group block"
          >
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#b58dd9] to-[#e8d1f3] px-10 py-14 md:p-16">
              <div className="pointer-events-none absolute -right-16 -top-16 size-72 rounded-full bg-white/20" />
              <div className="pointer-events-none absolute -bottom-20 right-32 size-64 rounded-full bg-white/10" />
              <div className="pointer-events-none absolute -bottom-10 right-10 size-40 rounded-full bg-white/15" />

              <div className="relative flex items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">創造の源泉を辿る</h2>
                  <p className="mt-3 text-base text-gray-800">Influence Map</p>
                </div>

                <div className="hidden flex-1 md:block">
                  <p className="text-sm leading-relaxed text-gray-800">
                    クリエイターたちの影響関係をインタラクティブに辿る
                  </p>
                </div>

                <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-white shadow-md transition-transform group-hover:scale-110">
                  <span className="text-lg text-gray-900">→</span>
                </div>
              </div>
            </div>
          </Link>
        </div> */}

        {/* Influence Map カード内容（デザイン確認用） */}
        <div className="mt-16 flex flex-col border-t border-GRAY_3 px-16 py-12 sm:flex-row sm:items-center lg:px-40">
          <div className="flex-1">
            <p className="text-xs tracking-widest text-GRAY_2">Influence Map</p>
            <h2 className="mt-3 text-xl font-bold">創造の源泉を辿る</h2>
            <p className="mt-6 text-sm leading-6 text-GRAY_1">
              「あの監督は何を観て育ったんだろう？」
              <br />
              Influence Mapでは、偉大なクリエイターたちのルーツとなった作品を一覧で辿れます。
              <br />
              尊敬する作家と同じ作品に触れて、創作の解像度を高めましょう。
            </p>
          </div>
          <Link
            href="/influence"
            className="mt-6 flex size-14 shrink-0 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-110 sm:mt-0"
          >
            <span className="text-lg">→</span>
          </Link>
        </div>

        {/* i-mobile広告（幅制限あり） */}
        <div className="mx-auto w-full px-7 py-12 md:max-w-screen-sm lg:max-w-screen-md">
          <IMobileResponsiveBigBannerAd
            pcAdElementId={AD_ELEMENT_ID_BIG_BANNER_1}
            pcAsid={ASID_BIG_BANNER_1}
            spAdElementId={AD_ELEMENT_ID_SP_BIG_BANNER_1}
            spAsid={ASID_SP_BIG_BANNER_1}
          />
        </div>
      </div>
    </>
  );
}
